import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { message } from 'antd';
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon, DocumentMagnifyingGlassIcon } from '@heroicons/react/24/outline';
import releaseNoteAdminApi from '../../services/releaseNoteAdminApi';
import releaseNoteApi from '../../services/releaseNoteApi';
import { TWButton, TWInput, TWConfirmModal, StatusBadge, SectionCard, FieldLabel, EmptyState, UserAvatar } from '../../components/ui';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import { validateUploadFile } from '../../utils/fileValidation';
import { releaseNoteStatusMap, publisherName, apiErrorText, isForbidden, isNotFound, ADMIN_PATH, FEED_PATH } from '../release-notes/release-note-model';
import {
  emptyForm, formFromDetail, addBlock, removeBlock, moveBlock, updateBlock, attachMedia, addTag, removeTag,
  validateForm, isValid, toUpsertRequest, TITLE_MAX, PUBLISHER_MAX, ACCEPT_IMAGE, MAX_IMAGE,
} from './release-note-editor-model';
import useCanManageReleaseNotes from './use-can-manage';
import AccessDenied, { ADMIN_SHELL_CLASS } from './components/AccessDenied';
import TagsInput from './components/TagsInput';
import BlockCard from './components/BlockCard';
import AddBlockBar from './components/AddBlockBar';
import EditorPreview from './components/EditorPreview';

const NO_ERRORS = { blocks: {} };
const SAVED_CAPTION_MS = 4000;
const PUBLISHER_HINT_NEW = 'The name readers see on this note. Leave empty to use your own name when you publish.';
const PUBLISHER_HINT_SAVED = 'The name readers see on this note. If you clear it, the saved name stays.';
// The publisher photo (contract 2026-10-21); the two refusals are MediaField's.
const PHOTO_HINT = 'jpeg, png, webp or gif · up to 6 MB. Shown beside your name on the note.';
const PHOTO_HINT_UNSAVED = 'Save the draft first to add a photo.';
const PHOTO_TYPE_ERROR = 'Use jpeg, png, webp or gif';
const PHOTO_SIZE_ERROR = 'Images can be at most 6 MB';
const areaCls =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400';

// New (`/new`) and existing (`/:id`) notes share this page (spec §7.3–§7.4).
// Every write answers the full ReleaseNoteDetail, which replaces local state
// unless the user typed while the write was in flight (see adopt()).
const ReleaseNoteEditor = () => {
  const allowed = useCanManageReleaseNotes();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [note, setNote] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState(NO_ERRORS);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [loadFailed, setLoadFailed] = useState(false);
  const [missing, setMissing] = useState(false);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  // 'upload' | 'remove' while the publisher photo is written, else null.
  const [photoBusy, setPhotoBusy] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const photoInputRef = useRef(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [justSaved, setJustSaved] = useState(false);
  const savedTimer = useRef(null);
  // The latest form, read synchronously by an upload's continuation.
  const formRef = useRef(form);
  formRef.current = form;
  // Set synchronously by adopt(): the data router re-renders with the new :id
  // before React has committed `note`, and that render must not re-read it.
  const adoptedIdRef = useRef(null);
  // Bumped by every load and every adopt, so a superseded load is ignored.
  const loadSeq = useRef(0);
  // Set by every edit and cleared when a read or write sequence starts: true
  // when the user typed while the request was in flight.
  const editedSinceSendRef = useRef(false);

  // The server's copy replaces local state after every read and write, unless
  // the user edited while the request ran: then the typed form stays (still
  // dirty) and only the server-owned slug of a new note is filled in.
  // Returns true when the server copy fully replaced the form.
  const adopt = (detail) => {
    loadSeq.current += 1;
    adoptedIdRef.current = detail.id;
    setLoading(false);
    setNote(detail);
    setErrors(NO_ERRORS);
    if (editedSinceSendRef.current) {
      setForm((prev) => ({ ...prev, slug: prev.slug || detail.slug }));
      setDirty(true);
      return false;
    }
    setForm(formFromDetail(detail));
    setDirty(false);
    return true;
  };

  // "Saved just now" (spec §7.3) — after a write only, never after the load.
  const flashSaved = () => {
    setJustSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setJustSaved(false), SAVED_CAPTION_MS);
  };
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const edit = (updater) => {
    setForm((prev) => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));
    editedSinceSendRef.current = true;
    setDirty(true);
    setJustSaved(false);
  };

  const fail = (err, fallback) => {
    if (isForbidden(err)) setDenied(true);
    message.error(apiErrorText(err, fallback));
  };

  useEffect(() => {
    if (!allowed) return;
    releaseNoteApi.tags().then((res) => setSuggestions((res.data?.data || []).map((t) => t.name))).catch(() => {});
  }, [allowed]);

  const load = useCallback(async () => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setLoading(true);
    setLoadFailed(false);
    setMissing(false);
    editedSinceSendRef.current = false;
    try {
      const res = await releaseNoteAdminApi.getById(id);
      const detail = res.data?.data;
      // A 200 without a note goes to the Retry view, never into adopt().
      if (!detail) throw new Error('Empty answer');
      if (seq === loadSeq.current) adopt(detail);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      if (isNotFound(err)) setMissing(true);
      else {
        if (!isForbidden(err)) setLoadFailed(true);
        fail(err, "Couldn't load this note");
      }
    } finally {
      // adopt() ends the loading state on success (and bumps loadSeq); a
      // failure of the current load ends it here.
      if (seq === loadSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!allowed || isNew) return;
    // After the first save the URL moves from /new to /:id; whether the router
    // remounts or reuses this instance, the note just adopted is on screen.
    if (adoptedIdRef.current === id || note?.id === id) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, id]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Returns the saved detail, or null when validation or the API refused.
  const save = useCallback(async () => {
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (!isValid(nextErrors)) {
      message.error('Fix the highlighted fields');
      return null;
    }
    setSaving(true);
    editedSinceSendRef.current = false;
    try {
      const res = await releaseNoteAdminApi.upsert(toUpsertRequest(form, note?.id ?? null));
      const detail = res.data.data;
      if (adopt(detail)) flashSaved();
      message.success(detail.status === 10 ? 'Saved' : 'Draft saved');
      if (isNew) navigate(`${ADMIN_PATH}/${detail.id}`, { replace: true });
      return detail;
    } catch (err) {
      fail(err, "Couldn't save the note");
      return null;
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, note, isNew, navigate]);

  // Publish and Unpublish are symmetric: both save a dirty form first, so the
  // status flip (which adopts the server copy) never discards an edit.
  // The edited-meanwhile flag is cleared once for the whole sequence, not again
  // before the flip: text typed during the save leg must survive the flip too.
  const togglePublish = async () => {
    setPublishing(true);
    editedSinceSendRef.current = false;
    try {
      let current = note;
      if (dirty || !current) {
        current = await save();
        if (!current) return;
      }
      if (current.status === 10) {
        const res = await releaseNoteAdminApi.unpublish(current.id);
        if (adopt(res.data.data)) flashSaved();
        message.success('Unpublished');
      } else {
        const res = await releaseNoteAdminApi.publish(current.id);
        if (adopt(res.data.data)) flashSaved();
        message.success('Published');
      }
    } catch (err) {
      fail(err, "Couldn't change the publish state");
    } finally {
      setPublishing(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await releaseNoteAdminApi.remove(note.id);
      message.success('Note deleted');
      setDirty(false);
      navigate(ADMIN_PATH, { replace: true });
    } catch (err) {
      fail(err, "Couldn't delete the note");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // Keyed by block, not index: the block may be moved or removed while the
  // upload is in flight.
  const upload = async (key, file) => {
    if (!note?.id) return;
    setUploadingKey(key);
    try {
      const res = await releaseNoteAdminApi.uploadMedia(note.id, file);
      const media = res.data.data;
      if (!formRef.current.blocks.some((b) => b.key === key)) {
        message.error('The block was removed before the upload finished.');
        return;
      }
      edit((prev) => {
        const i = prev.blocks.findIndex((b) => b.key === key);
        return i === -1 ? prev : attachMedia(prev, i, media);
      });
    } catch (err) {
      fail(err, 'Upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  // The publisher photo is server state on the note, not form state: an
  // upload or a remove patches `note` and never marks the form dirty (a later
  // save adopts a detail that carries the same fields). Upload and remove are
  // a symmetric pair: same guard, same busy slot, same error line.
  const uploadPhoto = async (file) => {
    if (!note?.id) return;
    setPhotoBusy('upload');
    try {
      const res = await releaseNoteAdminApi.uploadMedia(note.id, file, { purpose: 'publisher-photo' });
      const media = res.data.data;
      setNote((prev) => (prev ? { ...prev, publisherphotourl: media.url, publisherphotomediaid: media.id } : prev));
      setPhotoError('');
      message.success('Photo updated');
    } catch (err) {
      fail(err, 'Upload failed');
      setPhotoError(apiErrorText(err, 'Upload failed'));
    } finally {
      setPhotoBusy(null);
    }
  };

  const removePhoto = async () => {
    if (!note?.id || !note.publisherphotomediaid) return;
    setPhotoBusy('remove');
    try {
      await releaseNoteAdminApi.deleteMedia(note.publisherphotomediaid);
      setNote((prev) => (prev ? { ...prev, publisherphotourl: null, publisherphotomediaid: null } : prev));
      setPhotoError('');
      message.success('Photo removed');
    } catch (err) {
      fail(err, "Couldn't remove the photo");
      setPhotoError(apiErrorText(err, "Couldn't remove the photo"));
    } finally {
      setPhotoBusy(null);
    }
  };

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const result = validateUploadFile(file, { accept: ACCEPT_IMAGE, maxBytes: MAX_IMAGE });
    if (!result.ok) {
      const text = result.reason === 'size' ? PHOTO_SIZE_ERROR : PHOTO_TYPE_ERROR;
      setPhotoError(text);
      message.error(text);
      return;
    }
    setPhotoError('');
    uploadPhoto(file);
  };

  const leave = () => {
    if (dirty) setConfirmLeave(true);
    else navigate(ADMIN_PATH);
  };

  if (!allowed || denied) return <AccessDenied />;
  if (missing) {
    return (
      <AccessDenied
        icon={DocumentMagnifyingGlassIcon}
        title="This note no longer exists"
        description="It was deleted, or the link is wrong."
        backTo={ADMIN_PATH}
        backLabel="Back to Release Notes"
      />
    );
  }

  const isPublished = note?.status === 10;
  // Uploads are serialised with every write: a write adopts the server copy,
  // which re-keys the blocks, so an upload must never be in flight across one.
  // The publisher photo's upload and remove count the same way.
  const uploading = uploadingKey !== null || photoBusy !== null;
  const busy = saving || publishing || deleting || loading || uploading;
  const photoUrl = note?.publisherphotourl || undefined;
  const canPickPhoto = Boolean(note?.id) && !busy;

  return (
    <div className={ADMIN_SHELL_CLASS}>
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <button
          type="button"
          onClick={leave}
          className="inline-flex items-center gap-1 self-start rounded-sm text-xs font-semibold uppercase tracking-wider text-slate-500 transition hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-slate-400 dark:hover:text-slate-200 dark:focus-visible:ring-sky-400"
        >
          <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden />
          Release Notes
        </button>

        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">{isNew ? 'New note' : 'Edit note'}</h1>
            {note && !loading ? <StatusBadge map={releaseNoteStatusMap} status={note.status} size="sm" dot={isPublished} /> : null}
            {!loading && justSaved && !dirty ? <span className="text-xs text-slate-500 dark:text-slate-400">Saved just now</span> : null}
            {dirty ? <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span> : null}
          </div>
          {loadFailed ? null : (
            <div className="flex flex-wrap items-center gap-2">
              {isPublished ? (
                <Link to={`${FEED_PATH}/${note.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline dark:text-sky-400">
                  View note
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
                </Link>
              ) : null}
              {note ? (
                <TWButton variant="ghost" label="Delete" disabled={busy} onClick={() => setConfirmDelete(true)} className="!text-rose-600 hover:!bg-rose-50 dark:!text-rose-400 dark:hover:!bg-rose-900/20" />
              ) : null}
              <TWButton label="Save draft" loading={saving} disabled={busy} onClick={save} />
              <TWButton variant="primary" label={isPublished ? 'Unpublish' : 'Publish'} loading={publishing} disabled={busy} onClick={togglePublish} />
            </div>
          )}
        </header>

        {loading ? (
          <div className="space-y-3" aria-hidden="true">
            <SkeletonBlock className="h-10 w-full !rounded-md" />
            <SkeletonBlock className="h-24 w-full !rounded-md" />
            <SkeletonBlock className="h-40 w-full !rounded-md" />
          </div>
        ) : loadFailed ? (
          <EmptyState
            variant="dashed"
            title="Couldn't load this note"
            description="Check your connection and try again."
            action={<TWButton variant="primary" label="Retry" onClick={load} />}
          />
        ) : (
          <div className="grid items-start gap-8 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <div className="flex min-w-0 flex-col gap-6">
              <SectionCard>
                <div className="flex flex-col gap-4">
                  <TWInput
                    id="rn-title"
                    label="Title"
                    required
                    maxLength={TITLE_MAX}
                    value={form.title}
                    error={errors.title}
                    onChange={(e) => edit({ title: e.target.value })}
                    placeholder="What changed, in one line"
                  />
                  <div>
                    <FieldLabel htmlFor="rn-slug">Link</FieldLabel>
                    <div
                      className={`flex items-stretch overflow-hidden rounded-md border bg-white focus-within:ring-1 dark:bg-slate-900 ${
                        errors.slug
                          ? 'border-rose-400 focus-within:border-rose-500 focus-within:ring-rose-500 dark:border-rose-500/70'
                          : 'border-slate-300 focus-within:border-blue-500 focus-within:ring-blue-500 dark:border-slate-600 dark:focus-within:border-sky-400 dark:focus-within:ring-sky-400'
                      }`}
                    >
                      <span className="flex items-center whitespace-nowrap border-r border-slate-200 bg-slate-50 px-3 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">/release-notes/</span>
                      <input
                        id="rn-slug"
                        value={form.slug}
                        onChange={(e) => edit({ slug: e.target.value })}
                        aria-invalid={Boolean(errors.slug) || undefined}
                        aria-describedby="rn-slug-hint"
                        className="h-10 min-w-0 flex-1 bg-transparent px-3 text-sm text-slate-900 placeholder-slate-400 outline-none dark:text-slate-100 dark:placeholder-slate-500"
                        placeholder="generated-from-the-title"
                      />
                    </div>
                    <p id="rn-slug-hint" className={`mt-1 text-xs ${errors.slug ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'}`}>
                      {errors.slug || 'Leave empty to generate it from the title. Lowercase letters, numbers and hyphens, up to 80 characters.'}
                    </p>
                  </div>
                  <div>
                    <FieldLabel htmlFor="rn-summary">Summary</FieldLabel>
                    <textarea id="rn-summary" rows={3} className={areaCls} value={form.summary} onChange={(e) => edit({ summary: e.target.value })} placeholder="One or two sentences" />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Shown on the What's new feed and above the note. One or two sentences.</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                    <TWInput
                      id="rn-publisher-name"
                      label="Published by"
                      maxLength={PUBLISHER_MAX}
                      value={form.publishername}
                      hint={publisherName(note) ? PUBLISHER_HINT_SAVED : PUBLISHER_HINT_NEW}
                      onChange={(e) => edit({ publishername: e.target.value })}
                      placeholder="Your name"
                    />
                    <div className="flex flex-col">
                      <FieldLabel htmlFor="rn-publisher-photo">Publisher photo</FieldLabel>
                      <div className="flex items-center gap-3">
                        <span aria-hidden="true">
                          <UserAvatar name={form.publishername.trim() || publisherName(note)} src={photoUrl} size="lg" />
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            id="rn-publisher-photo"
                            ref={photoInputRef}
                            type="file"
                            accept={ACCEPT_IMAGE}
                            className="sr-only"
                            // No tab stop of its own: the visible Upload/Replace button clicks it.
                            tabIndex={-1}
                            onChange={pickPhoto}
                            disabled={!canPickPhoto}
                          />
                          <TWButton
                            size="sm"
                            label={photoBusy === 'upload' ? 'Uploading…' : photoUrl ? 'Replace photo' : 'Upload photo'}
                            loading={photoBusy === 'upload'}
                            disabled={!canPickPhoto}
                            onClick={() => photoInputRef.current?.click()}
                          />
                          {photoUrl ? (
                            <TWButton
                              size="sm"
                              variant="ghost"
                              label={photoBusy === 'remove' ? 'Removing…' : 'Remove'}
                              loading={photoBusy === 'remove'}
                              disabled={busy}
                              onClick={removePhoto}
                              className="!text-rose-600 hover:!bg-rose-50 dark:!text-rose-400 dark:hover:!bg-rose-900/20"
                            />
                          ) : null}
                        </div>
                      </div>
                      <p className={`mt-1 text-xs ${photoError ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'}`}>
                        {photoError || (note?.id ? PHOTO_HINT : PHOTO_HINT_UNSAVED)}
                      </p>
                    </div>
                  </div>
                  <TagsInput tags={form.tags} suggestions={suggestions} onAdd={(t) => edit((prev) => addTag(prev, t))} onRemove={(t) => edit((prev) => removeTag(prev, t))} />
                </div>
              </SectionCard>

              <section className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-3">
                  <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Content</h2>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{form.blocks.length} {form.blocks.length === 1 ? 'block' : 'blocks'} · drawn top to bottom</span>
                </div>
                {form.blocks.map((block, index) => (
                  <BlockCard
                    key={block.key}
                    block={block}
                    index={index}
                    count={form.blocks.length}
                    error={errors.blocks[index]}
                    canUpload={Boolean(note?.id) && !busy}
                    saved={Boolean(note?.id)}
                    uploading={uploadingKey === block.key}
                    locked={uploading}
                    onChange={(patch) => edit((prev) => updateBlock(prev, index, patch))}
                    onMove={(delta) => edit((prev) => moveBlock(prev, index, delta))}
                    onRemove={() => edit((prev) => removeBlock(prev, index))}
                    onPick={(file) => upload(block.key, file)}
                  />
                ))}
                <AddBlockBar mediaEnabled={Boolean(note?.id)} locked={uploading} onAdd={(type) => edit((prev) => addBlock(prev, type))} />
              </section>
            </div>

            <EditorPreview form={form} note={note} />
          </div>
        )}
      </div>

      <TWConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        tone="danger"
        title="Delete this note?"
        description="Readers lose the link; its images and videos are removed."
        confirmLabel="Delete"
        loading={deleting}
      />
      <TWConfirmModal
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        onConfirm={() => {
          setConfirmLeave(false);
          setDirty(false);
          navigate(ADMIN_PATH);
        }}
        tone="warning"
        title="Discard unsaved changes?"
        description="Your edits since the last save will be lost."
        confirmLabel="Discard"
      />
    </div>
  );
};

export default ReleaseNoteEditor;
